/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */
import noop from 'lodash/noop';

import Store from 'state/Store';
import { getAccountsById } from 'state/account/AccountSelectors';
import { getAccountDescriptor } from 'services/Accounts';
import {
  captureGVABusinessEvent,
  updatePreferencesViewRelationships
} from 'services/Rest';
import {
  CORPORATE,
  KEOGH,
  NON_PROFIT_ORGANIZATION,
  ESTATE,
  ORGANIZATION,
  ROTH_IRA_DECEASED,
  TRADITIONAL_IRA_DECEASED,
  ROTH_IRA_BENE_DEATH,
  NON_FIDUCIARY_RETIREMENT_PLAN,
  SEP_IRA,
  SIMPLE_IRA,
  INDIVIDUAL_401K
} from 'services/AccountTypes';
import { rothInheritedAccountsGVAEnabled } from 'services/FeatureFlags';

export const GRANT_VIEW_EVENT_TYPES = {
  ADD: 'ADD_GRANT_VIEW_ACCESS',
  UPDATE: 'UPDATE_GRANT_VIEW_ACCESS',
  DELETE: 'DELETE_GRANT_VIEW_ACCESS'
};

export const GRANT_VIEW_ACCESS_RATE_LIMIT_ERROR = '429';

const getDisposition = (action) => {
  return {
    action
  };
};

// consider moving to service utility as we build out more events and replace data as a param
export const buildBusinessEvent = (
  eventType,
  email,
  customer,
  accounts,
  view
) => {
  const {
    hasActivePlusOnly,
    hasMaryland529Only,
    hasAlaska529Only,
    hasNational529Only,
    has529Only,
    hasMaryland529,
    hasNational529,
    hasAlaska529
  } = view;

  const clientSegment = hasMaryland529Only
    ? 'MD529'
    : hasAlaska529Only
      ? 'UA529'
      : hasNational529Only
        ? 'TRP529'
        : 'RETAIL';

  const formattedAccounts = accounts.map((acct) => {
    const { accountName, accountNumber } = acct;
    return {
      accountName,
      accountNumber
    };
  });

  const eventPayload = {
    eventType: eventType,
    emailAddress: email,
    brandingProfile: clientSegment,
    entityKey: {
      businessPartyId: customer.businessPartyId,
      uberId: customer.uberId
    },
    data: {
      type: 'GrantViewEventData',
      grantAccessRelationships: [
        {
          recipientType: 'grantor',
          recipientEntityKey: {
            businessPartyId: customer.businessPartyId,
            uberId: customer.uberId
          },
          recipientEmailAddress: customer.emailAddress,
          hasOnlyManagedAccounts: hasActivePlusOnly,
          hasOnlyAlaska529: hasAlaska529Only,
          hasOnlyMaryland529: hasMaryland529Only,
          hasOnlyAlaska529AndNational529:
            has529Only && hasAlaska529 && hasNational529 && !hasMaryland529,
          hasOnlyMaryland529AndNational529:
            has529Only && hasMaryland529 && hasNational529 && !hasAlaska529,
          hasOnlyNational529: hasNational529Only
        }
      ],
      accounts: formattedAccounts
    }
  };
  return eventPayload;
};

export const addAccountViewAccess = (
  customer,
  accountSelections,
  successCallback,
  email,
  user,
  view
) => {
  const accounts = [];
  const accountsById = getAccountsById(Store.getState());
  const viewAccesses = Object.keys(accountsById).reduce((result, accountId) => {
    if (accountSelections[accountId]) {
      const account = accountsById[accountId];
      accounts.push(account);
      result.push({
        accountDescriptor: getAccountDescriptor(account),
        disposition: getDisposition('add')
      });
    }
    return result;
  }, []);

  const payload = {
    customer,
    viewAccesses
  };
  const event = buildBusinessEvent(
    GRANT_VIEW_EVENT_TYPES.ADD,
    email,
    user,
    accounts,
    view
  );

  const callback = () => {
    captureGVABusinessEvent(event);
    successCallback();
  }

  updatePreferencesViewRelationships(payload, callback, true)
};

export const updateAccountViewAccess = (
  customer,
  accountSelections,
  successCallback = noop
) => {
  const accountsById = getAccountsById(Store.getState());

  const viewAccesses = Object.entries(accountSelections).reduce(
    (result, [accountId, disposition]) => {
      const account = accountsById[accountId];
      if (account) {
        result.push({
          accountDescriptor: getAccountDescriptor(account),
          disposition
        });
      }
      return result;
    },
    []
  );

  const payload = {
    customer,
    viewAccesses
  };
  updatePreferencesViewRelationships(payload, successCallback);
};

export const deleteUserPayload = (customer, accounts) => {
  // flatten list of accounts
  const fullAccountList = Object.values(accounts).reduce((acc, acctGroup) => {
    acctGroup.forEach((acct) => acc.push(acct));
    return acc;
  }, []);

  const accountsToDelete = fullAccountList.map((account) => {
    return {
      accountDescriptor: getAccountDescriptor(account),
      disposition: { action: 'remove' }
    };
  });
  return {
    bepAccounts: fullAccountList,
    customer: {
      ...customer
    },
    viewAccesses: accountsToDelete
  };
};

export const isNotQualified = (accountTypeId) => {
  let notQualifiedAccountTypes = [
    CORPORATE,
    KEOGH,
    NON_PROFIT_ORGANIZATION,
    ESTATE,
    ORGANIZATION,
    ROTH_IRA_DECEASED,
    TRADITIONAL_IRA_DECEASED,
    ROTH_IRA_BENE_DEATH,
    NON_FIDUCIARY_RETIREMENT_PLAN,
    SEP_IRA,
    SIMPLE_IRA,
    INDIVIDUAL_401K
  ];
  if(rothInheritedAccountsGVAEnabled()) {
    notQualifiedAccountTypes = notQualifiedAccountTypes.filter(accountType => accountType !== ROTH_IRA_BENE_DEATH);
  }
  return notQualifiedAccountTypes.some(
    (notQualifiedAccountType) => notQualifiedAccountType === accountTypeId
  );
};

